import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { CheckCircleOutlined, FileExcelOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import {
  staffAttendanceApi,
  type StaffAttendanceRow,
  type StaffStatus,
} from '@/api/hr-attendance';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';
import { downloadFile } from '@/shared/lib/download';

type RowState = StaffAttendanceRow;

export function StaffAttendancePage() {
  const [search, setSearch] = useSearchParams();
  const view = search.get('view') ?? 'daily';
  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / HR"
        title="Staff attendance"
        description="Daily attendance grid + monthly summary. Holidays and Sundays are excluded from working days."
      />
      <Tabs
        activeKey={view}
        onChange={(k) => setSearch({ view: k })}
        items={[
          { key: 'daily', label: 'Daily', children: <DailyTab /> },
          { key: 'month', label: 'Monthly summary', children: <MonthTab /> },
        ]}
      />
    </div>
  );
}

function DailyTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canSave = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [date, setDate] = useState<Dayjs>(dayjs());
  const [rows, setRows] = useState<RowState[]>([]);
  const [search, setSearch] = useState('');

  const { data, isFetching } = useQuery({
    queryKey: ['hr', 'attendance', 'sheet', date.format('YYYY-MM-DD')],
    queryFn: () => staffAttendanceApi.sheet(date.format('YYYY-MM-DD')),
  });

  useEffect(() => {
    if (!data) {
      setRows([]);
      return;
    }
    setRows(data.rows.map((r) => ({ ...r })));
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      staffAttendanceApi.save({
        date: date.format('YYYY-MM-DD'),
        entries: rows
          .filter((r) => r.status != null)
          .map((r) => ({
            staffId: r.staffId,
            status: r.status!,
            halfDayType: r.status === 'HALF_DAY' ? r.halfDayType ?? 'MORNING' : undefined,
            reason: r.reason ?? undefined,
          })),
      }),
    onSuccess: () => {
      message.success('Saved');
      qc.invalidateQueries({ queryKey: ['hr', 'attendance', 'sheet'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const setAll = (status: StaffStatus) => {
    setRows((cur) => cur.map((r) => ({ ...r, status, halfDayType: status === 'HALF_DAY' ? r.halfDayType ?? 'MORNING' : null })));
  };

  const update = (staffId: number, patch: Partial<RowState>) => {
    setRows((cur) => cur.map((r) => (r.staffId === staffId ? { ...r, ...patch } : r)));
  };

  const filtered = search.trim() === ''
    ? rows
    : rows.filter((r) =>
        (r.staffName ?? '').toLowerCase().includes(search.trim().toLowerCase()) ||
        (r.designationName ?? '').toLowerCase().includes(search.trim().toLowerCase()),
      );

  const columns: TableProps<RowState>['columns'] = [
    { title: '#', width: 50, render: (_, _row, idx) => idx + 1 },
    {
      title: 'Staff',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.staffName}</Typography.Text>
          <Space size={6}>
            {row.designationName && <Tag>{row.designationName}</Tag>}
            {row.departmentName && <Tag>{row.departmentName}</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Status',
      width: 200,
      render: (_, row) => (
        <Segmented
          size="small"
          value={row.status ?? ''}
          onChange={(v) => {
            const s = v === '' ? null : (v as StaffStatus);
            update(row.staffId, {
              status: s,
              halfDayType: s === 'HALF_DAY' ? row.halfDayType ?? 'MORNING' : null,
            });
          }}
          options={[
            { value: 'PRESENT', label: 'P' },
            { value: 'ABSENT', label: 'A' },
            { value: 'LATE', label: 'L' },
            { value: 'HALF_DAY', label: '½' },
            { value: 'LEAVE', label: 'Lv' },
          ]}
          disabled={!canSave}
        />
      ),
    },
    {
      title: 'Half-day',
      width: 130,
      render: (_, row) =>
        row.status === 'HALF_DAY' ? (
          <Select
            size="small"
            value={row.halfDayType ?? 'MORNING'}
            onChange={(v) => update(row.staffId, { halfDayType: v })}
            options={[
              { value: 'MORNING', label: 'Morning' },
              { value: 'AFTERNOON', label: 'Afternoon' },
            ]}
            style={{ width: '100%' }}
          />
        ) : null,
    },
    {
      title: 'Reason',
      render: (_, row) =>
        ['ABSENT', 'LATE', 'LEAVE'].includes(row.status ?? '') ? (
          <Input
            size="small"
            value={row.reason ?? ''}
            onChange={(e) => update(row.staffId, { reason: e.target.value })}
            placeholder="optional note"
          />
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Space wrap size={12}>
        <DatePicker
          value={date}
          onChange={(d) => d && setDate(d)}
          format="DD MMM YYYY"
          allowClear={false}
        />
        <Input.Search
          allowClear
          placeholder="Search name / designation"
          style={{ width: 240 }}
          onSearch={(v) => setSearch(v)}
          onChange={(e) => setSearch(e.target.value)}
        />
        {canSave && rows.length > 0 && (
          <>
            <Button onClick={() => setAll('PRESENT')} icon={<CheckCircleOutlined />}>Mark all present</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={save.isPending}
              onClick={() => save.mutate()}
              disabled={!rows.some((r) => r.status != null)}
            >
              Save
            </Button>
          </>
        )}
      </Space>

      {data && (
        <Space wrap size={[14, 4]}>
          <Tag>Total: {data.totalStaff}</Tag>
          <Tag color="success">Present: {data.present}</Tag>
          <Tag color="error">Absent: {data.absent}</Tag>
          <Tag color="warning">Late: {data.late}</Tag>
          <Tag color="processing">Half-day: {data.halfDay}</Tag>
          <Tag>Leave: {data.leave}</Tag>
          <Tag>Not marked: {data.notMarked}</Tag>
        </Space>
      )}

      {data && data.totalStaff === 0 ? (
        <Empty description="No active staff" />
      ) : (
        <Table
          rowKey="staffId"
          size="small"
          loading={isFetching}
          columns={columns}
          dataSource={filtered}
          pagination={false}
        />
      )}

      {data && data.rows.length > 0 && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Existing marks shown above. Use the radio buttons to set status; edited rows are flagged as manual override on save.
        </Typography.Text>
      )}
    </div>
  );
}

function MonthTab() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);

  const { data, isFetching } = useQuery({
    queryKey: ['hr', 'attendance', 'summary', range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')],
    queryFn: () => staffAttendanceApi.summary(range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')),
  });

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {data && (
          <Tag color="processing" style={{ fontSize: 13 }}>
            Working days: {data.workingDays}
          </Tag>
        )}
        <Button
          icon={<FileExcelOutlined />}
          disabled={!data}
          onClick={() =>
            downloadFile('/reports/exports/staff-attendance-monthly.xlsx',
              { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') },
              `staff-attendance-${range[0].format('YYYY-MM-DD')}-to-${range[1].format('YYYY-MM-DD')}.xlsx`)
          }
        >
          Export Excel
        </Button>
      </Space>

      {data && data.rows.length === 0 && !isFetching ? (
        <Empty description="No active staff" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="staffId"
            size="small"
            loading={isFetching}
            dataSource={data?.rows ?? []}
            pagination={{ pageSize: 50 }}
            columns={[
              { title: 'Staff', dataIndex: 'staffName' },
              { title: 'Present', dataIndex: 'present', width: 90, align: 'right' },
              { title: 'Absent', dataIndex: 'absent', width: 90, align: 'right' },
              { title: 'Late', dataIndex: 'late', width: 90, align: 'right' },
              { title: 'Half-day', dataIndex: 'halfDay', width: 100, align: 'right' },
              { title: 'Leave', dataIndex: 'leave', width: 90, align: 'right' },
              { title: 'Not marked', dataIndex: 'notMarked', width: 110, align: 'right' },
              {
                title: '%',
                width: 100,
                align: 'right',
                render: (_, row) => {
                  const denom = (data?.workingDays ?? 0) || 1;
                  const pct = ((row.present + row.late + row.halfDay * 0.5) / denom) * 100;
                  return <Tag color={pct >= 90 ? 'success' : pct >= 75 ? 'processing' : 'warning'}>{pct.toFixed(1)}%</Tag>;
                },
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
